import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Alert, Table, Modal, Badge, Form, ButtonGroup } from "react-bootstrap";
import { 
  PersonPlus, 
  CheckCircle, 
  XCircle, 
  Eye, 
  People,
  Book,
  Calendar2Check,
  ExclamationTriangle,
  BarChart,
  Activity
} from 'react-bootstrap-icons';

// Import your existing API functions
import { 
  getClassesBySchoolId,
  getAssessmentsByStudentId,
  getAssessmentTypes,
  getStudentsForClass,
  assignAssessment
} from "../services/APIClient";

function ClassScreenerAssignment({ schoolId, schoolName, onClose }) {
  // State management
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classStudents, setClassStudents] = useState([]);
  const [assessments, setAssessments] = useState({
    grade1_standard: [],
    grade1_custom: [],
    grade2_standard: [],
    grade2_custom: [],
    rti: [], // New RTI assessments
    general: []
  });
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentAssessments, setStudentAssessments] = useState({});
  
  // New state for assessment type toggle
  const [assessmentMode, setAssessmentMode] = useState('standard'); // 'standard' or 'rti'
  
  // Search states
  const [classSearch, setClassSearch] = useState('');
  const [assessmentSearch, setAssessmentSearch] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loadingStudentData, setLoadingStudentData] = useState(false);
  const [loadingAssessments, setLoadingAssessments] = useState(false);

  // Load classes when component mounts
  useEffect(() => {
    if (schoolId) {
      loadClasses();
    }
  }, [schoolId]);

  // Load assessments when component mounts
  useEffect(() => {
    loadAssessments();
  }, []);

  // Load student data when class is selected
  useEffect(() => {
    if (selectedClass) {
      loadClassStudents();
    }
  }, [selectedClass]);

  // Load individual student assessments when students are loaded
  useEffect(() => {
    if (classStudents.length > 0) {
      loadStudentAssessments();
    }
  }, [classStudents]);

  // Reset selection when switching between modes
  useEffect(() => {
    setSelectedAssessment(null);
    setSelectedStudents([]);
    setAssessmentSearch(''); // Clear search when switching modes
  }, [assessmentMode]);

  // Clear searches when selections change
  useEffect(() => {
    setStudentSearch('');
  }, [selectedClass]);

  useEffect(() => {
    setAssessmentSearch('');
  }, [selectedClass]);

  const loadClasses = async () => {
    try {
      setLoading(true);
      console.log('Loading classes for school ID:', schoolId);
      const response = await getClassesBySchoolId(schoolId);
      console.log('Classes loaded:', response);
      setClasses(response);
      setError(null);
    } catch (err) {
      console.error('Error loading classes:', err);
      setError("Failed to load classes for this school");
    } finally {
      setLoading(false);
    }
  };

  const loadClassStudents = async () => {
    try {
      setLoadingStudentData(true);
      console.log('Loading students for class:', selectedClass);
      
      const response = await getStudentsForClass(selectedClass.id);
      console.log('Students response:', response);
      
      const students = response.students || response || [];
      
      // Ensure students is always an array
      const studentsArray = Array.isArray(students) ? students : [];
      console.log('Final students array:', studentsArray);
      
      setClassStudents(studentsArray);
      setSelectedStudents([]);
      setError(null);
    } catch (err) {
      console.error('Error loading students:', err);
      setError("Failed to load students for this class");
      setClassStudents([]); // Ensure it's an array even on error
    } finally {
      setLoadingStudentData(false);
    }
  };

  const loadAssessments = async () => {
    try {
      const response = await getAssessmentTypes();
      console.log('All assessments loaded:', response);
      
      // Filter out task-type assessments
      const filteredAssessments = response.filter(assessment => assessment.task !== true);
      
      // Separate assessments by type and grade level
      const categorizedAssessments = {
        grade1_standard: [],
        grade1_custom: [],
        grade2_standard: [],
        grade2_custom: [],
        rti: [], // New RTI category
        general: []
      };

      // Grade 2 assessment IDs
      const gradeLevel2Ids = new Set([
        "927b2379-998c-4424-af56-1f82e0144765",
        "9b962947-4f59-4e19-a725-40601b66e8ff",
        "08e97976-f32f-4ad6-a2df-7a2be0789d70",
      ]);

      filteredAssessments.forEach(assessment => {
        // Check if it's an RTI assessment first
        if (assessment.rti === true) {
          categorizedAssessments.rti.push(assessment);
          return;
        }

        // For non-RTI assessments, check grade level compatibility
        // Grade 2 assessment IDs (legacy check)
        const isGrade2Legacy = gradeLevel2Ids.has(assessment.id);
        
        // Check assessment grade level flags
        const isGrade1Assessment = assessment.grade_level1 === true;
        const isGrade2Assessment = assessment.grade_level2 === true || isGrade2Legacy;
        
        // Determine if it's custom
        const isCustom = assessment.custom === true || assessment.name.toLowerCase().includes('custom');
        
        if (isGrade2Assessment) {
          if (isCustom) {
            categorizedAssessments.grade2_custom.push(assessment);
          } else {
            categorizedAssessments.grade2_standard.push(assessment);
          }
        } else if (isGrade1Assessment) {
          if (isCustom) {
            categorizedAssessments.grade1_custom.push(assessment);
          } else {
            categorizedAssessments.grade1_standard.push(assessment);
          }
        } else {
          // Neither grade level flag is set - put in general or grade 1 as fallback
          if (isCustom) {
            categorizedAssessments.grade1_custom.push(assessment);
          } else {
            categorizedAssessments.grade1_standard.push(assessment);
          }
        }
      });

      console.log('Categorized assessments:', categorizedAssessments);
      setAssessments(categorizedAssessments);
    } catch (err) {
      console.error('Error loading assessments:', err);
      setError("Failed to load available assessments");
    }
  };

  const loadStudentAssessments = async () => {
    try {
      setLoadingAssessments(true);
      console.log('Loading assessments for students:', classStudents);
      const assessmentData = {};
      
      // Ensure classStudents is an array before iterating
      if (!Array.isArray(classStudents)) {
        console.warn('classStudents is not an array:', classStudents);
        setStudentAssessments({});
        return;
      }
      
      for (const student of classStudents) {
        try {
          console.log(`Fetching assessments for student ${student.id} (${student.name})`);
          const studentAssessments = await getAssessmentsByStudentId(schoolId, student.id);
          console.log(`Student ${student.name} assessments:`, studentAssessments);
          assessmentData[student.id] = studentAssessments;
        } catch (err) {
          console.warn(`Failed to load assessments for student ${student.id}:`, err);
          assessmentData[student.id] = [];
        }
      }
      
      console.log('All student assessment data:', assessmentData);
      setStudentAssessments(assessmentData);
    } catch (err) {
      console.error("Failed to load student assessment data:", err);
    } finally {
      setLoadingAssessments(false);
    }
  };

  // Grade abbreviations mapping
  const gradeAbbreviations = {
    "Standard 1": "S1",
    "Standard 2": "S2",
    Reception: "Recep",
    "Year 1": "Y1",
    "Primary 1": "P1",
    "Primary 2": "P2",
    "Pre-K": "Pre-K",
    "Kindergarten (K)": "K",
    "KG1 (Kindergarten 1)": "KG1",
    "KG2 (Kindergarten 2)": "KG2",
    "Junior Infants": "Jnr",
    "Senior Infants": "Snr",
  };

  // Function to replace assessment name prefix based on class grade
  const replacePrefix = (name) => {
    if (!selectedClass || assessmentMode === 'rti') return name; // Don't modify RTI assessment names
    
    if (selectedClass.grade_level1) {
      const grade1Abbrev = gradeAbbreviations[selectedClass.grade_level] || "Grade1";
      return name.replace("Junior", grade1Abbrev);
    } else if (selectedClass.grade_level2) {
      const grade2Abbrev = gradeAbbreviations[selectedClass.grade_level] || "Grade2";
      return name.replace("Senior", grade2Abbrev);
    }
    return name;
  };

  const handleStudentSelection = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleSelectAllStudents = () => {
    if (selectedStudents.length === getEligibleStudents().length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(getEligibleStudents().map(student => student.id));
    }
  };

  // Filter functions for search
  const getFilteredClasses = () => {
    if (!classSearch.trim()) return classes;
    
    return classes.filter(classItem => 
      classItem.name.toLowerCase().includes(classSearch.toLowerCase()) ||
      classItem.grade_level.toLowerCase().includes(classSearch.toLowerCase()) ||
      (classItem.teacherName && classItem.teacherName.toLowerCase().includes(classSearch.toLowerCase()))
    );
  };

  const getFilteredAssessments = (assessmentList) => {
    if (!assessmentSearch.trim()) return assessmentList;
    
    return assessmentList.filter(assessment =>
      assessment.name.toLowerCase().includes(assessmentSearch.toLowerCase()) ||
      (assessment.description && assessment.description.toLowerCase().includes(assessmentSearch.toLowerCase()))
    );
  };

  const getFilteredStudents = () => {
    if (!studentSearch.trim()) return classStudents;
    
    return classStudents.filter(student =>
      student.name.toLowerCase().includes(studentSearch.toLowerCase()) ||
      (student.grade && student.grade.toLowerCase().includes(studentSearch.toLowerCase()))
    );
  };

  // Helper function to identify RTI assessments
  const isRTIAssessment = (assessment) => {
    // Check multiple ways an assessment might be marked as RTI
    return assessment.rti === true || 
           assessment.assessment_type === 'rti' ||
           assessment.type === 'rti' ||
           (assessment.name && assessment.name.toLowerCase().includes('rti')) ||
           // Check if the assessment ID matches any from our RTI assessments list
           assessments.rti.some(rtiAssessment => 
             rtiAssessment.id === assessment.id ||
             rtiAssessment.id === assessment.assessment_id ||
             rtiAssessment.id === assessment.assessmentId ||
             rtiAssessment.id === assessment.assessmentid
           );
  };

  // FIXED: Updated getEligibleStudents function to properly prevent RTI duplicates
  const getEligibleStudents = () => {
    if (!selectedAssessment || !Array.isArray(classStudents)) return [];
    
    return classStudents.filter(student => {
      const studentAssessmentList = studentAssessments[student.id] || [];
      
      if (assessmentMode === 'rti') {
        // For RTI: Check if student has ANY pending RTI assessment
        const hasPendingRTIAssessment = studentAssessmentList.some(assessment => {
          const isIncomplete = assessment.completed !== true && 
                               assessment.status !== 'completed' &&
                               assessment.completion_status !== 'completed';
          
          return isIncomplete && isRTIAssessment(assessment);
        });
        
        return !hasPendingRTIAssessment; // Eligible if they don't have any pending RTI assessment
      } else {
        // For standard assessments: Check if student has ANY pending (incomplete) assessment
        const hasPendingAssessment = studentAssessmentList.some(assessment => {
          const isIncomplete = assessment.completed !== true && 
                              assessment.status !== 'completed' &&
                              assessment.completion_status !== 'completed';
          return isIncomplete;
        });
        
        return !hasPendingAssessment;
      }
    });
  };

  // Get assessments available for the selected class and mode
  const getAvailableAssessments = () => {
    if (assessmentMode === 'rti') {
      // Filter RTI assessments by grade level if a class is selected
      if (!selectedClass) return assessments.rti;
      
      return assessments.rti.filter(assessment => {
        // Check if RTI assessment is compatible with the selected class grade level
        if (selectedClass.grade_level1 && assessment.grade_level1) return true;
        if (selectedClass.grade_level2 && assessment.grade_level2) return true;
        
        // If no specific grade level flags on the RTI assessment, include it for all grades
        if (!assessment.grade_level1 && !assessment.grade_level2) return true;
        
        return false;
      });
    }

    if (!selectedClass) return [];
    
    let availableAssessments = [];
    
    if (selectedClass.grade_level1) {
      availableAssessments = [
        ...assessments.grade1_standard,
        ...assessments.grade1_custom
      ];
    } else if (selectedClass.grade_level2) {
      availableAssessments = [
        ...assessments.grade2_standard,
        ...assessments.grade2_custom
      ];
    } else {
      availableAssessments = [
        ...assessments.grade1_standard,
        ...assessments.grade1_custom,
        ...assessments.grade2_standard,
        ...assessments.grade2_custom,
        ...assessments.general
      ];
    }
    
    return availableAssessments;
  };

  // Group assessments by type for better display
  const getAssessmentGroups = () => {
    const availableAssessments = getAvailableAssessments();
    
    if (assessmentMode === 'rti') {
      return {
        rti: availableAssessments
      };
    }
    
    return {
      standard: availableAssessments.filter(a => !a.custom && !(a.name && a.name.toLowerCase().includes('custom'))),
      custom: availableAssessments.filter(a => a.custom === true || (a.name && a.name.toLowerCase().includes('custom')))
    };
  };

  const getStudentAssessmentStatus = (student) => {
    if (!selectedAssessment) return "not-selected";
    
    const studentAssessmentList = studentAssessments[student.id] || [];
    console.log(`Checking status for ${student.name}:`, {
      selectedAssessmentId: selectedAssessment.id,
      studentAssessments: studentAssessmentList,
      assessmentMode
    });
    
    if (assessmentMode === 'rti') {
      // For RTI: Check if they have ANY pending RTI assessment
      const hasPendingRTIAssessment = studentAssessmentList.some(assessment => {
        const isIncomplete = assessment.completed !== true && 
                           assessment.status !== 'completed' &&
                           assessment.completion_status !== 'completed';
        
        return isIncomplete && isRTIAssessment(assessment);
      });
      
      if (hasPendingRTIAssessment) {
        return "assigned";
      }
      
      // Check if they completed this specific RTI assessment
      const hasCompletedThisRTIAssessment = studentAssessmentList.find(assessment => {
        const idMatch = assessment.id === selectedAssessment.id || 
                       assessment.assessment_id === selectedAssessment.id ||
                       assessment.assessmentId === selectedAssessment.id ||
                       assessment.assessmentid === selectedAssessment.id ||
                       String(assessment.id) === String(selectedAssessment.id);
        
        if (idMatch) {
          const isCompleted = assessment.completed === true || 
                             assessment.status === 'completed' ||
                             assessment.completion_status === 'completed';
          return isCompleted;
        }
        return false;
      });
      
      if (hasCompletedThisRTIAssessment) {
        return "completed";
      }
      
      return "eligible";
    } else {
      // Standard assessment logic (existing)
      const hasPendingAssessment = studentAssessmentList.some(assessment => {
        const isIncomplete = assessment.completed !== true && 
                            assessment.status !== 'completed' &&
                            assessment.completion_status !== 'completed';
        return isIncomplete;
      });
      
      if (hasPendingAssessment) {
        return "assigned";
      }
      
      const hasCompletedThisAssessment = studentAssessmentList.find(assessment => {
        const idMatch = assessment.id === selectedAssessment.id || 
                       assessment.assessment_id === selectedAssessment.id ||
                       assessment.assessmentId === selectedAssessment.id ||
                       assessment.assessmentid === selectedAssessment.id ||
                       String(assessment.id) === String(selectedAssessment.id);
        
        if (idMatch) {
          const isCompleted = assessment.completed === true || 
                             assessment.status === 'completed' ||
                             assessment.completion_status === 'completed';
          return isCompleted;
        }
        return false;
      });
      
      if (hasCompletedThisAssessment) {
        return "completed";
      }
      
      return "eligible";
    }
  };

  const handleAssignScreeners = async () => {
    try {
      setLoading(true);
      
      const assignmentPromises = selectedStudents.map(studentId => {
        const student = Array.isArray(classStudents) ? classStudents.find(s => s.id === studentId) : null;
        const modifiedAssessmentName = replacePrefix(selectedAssessment.name);
        
        return assignAssessment(
          [{
            id: studentId,
            name: student?.name || 'Unknown Student'
          }],
          selectedAssessment.id,
          modifiedAssessmentName
        );
      });
      
      await Promise.all(assignmentPromises);
      
      const assessmentType = assessmentMode === 'rti' ? 'RTI assessment' : 'assessment';
      setSuccess(`Successfully assigned ${replacePrefix(selectedAssessment.name)} (${assessmentType}) to ${selectedStudents.length} students`);
      setShowConfirmModal(false);
      setSelectedStudents([]);
      
      await loadStudentAssessments();
    } catch (err) {
      console.error('Error assigning screeners:', err);
      setError("Failed to assign screeners to students");
    } finally {
      setLoading(false);
    }
  };

  const getSelectedStudentDetails = () => {
    // Ensure classStudents is an array
    if (!Array.isArray(classStudents)) {
      console.warn('classStudents is not an array in getSelectedStudentDetails:', classStudents);
      return [];
    }
    
    return classStudents
      .filter(student => selectedStudents.includes(student.id))
      .map((student, index) => ({
        index: index + 1,
        name: student.name,
        grade: student.grade,
        assessment: replacePrefix(selectedAssessment?.name) || "N/A",
        type: assessmentMode === 'rti' ? 'RTI' : 'Standard'
      }));
  };

  return (
    <Container fluid className="p-4">
      <div className="mb-4">
        <h3>Assign Screeners to Classes</h3>
        <p className="text-muted">School: <strong>{schoolName}</strong></p>
      </div>

      {/* Error and Success Messages */}
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      {success && <Alert variant="success" className="mb-3">{success}</Alert>}

      <Row>
        {/* Class Selection */}
        <Col md={4}>
          <Card className="mb-3">
            <Card.Header>
              <h5><People className="me-2" />Select Class</h5>
            </Card.Header>
            <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {/* Search input for classes */}
              <Form.Control
                type="text"
                placeholder="Search classes..."
                value={classSearch}
                onChange={(e) => setClassSearch(e.target.value)}
                className="mb-3"
                size="sm"
              />
              
              {loading ? (
                <div className="text-center p-3">
                  <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                  Loading classes...
                </div>
              ) : getFilteredClasses().length === 0 ? (
                <div>
                  {classSearch ? (
                    <p className="text-muted">No classes found matching "{classSearch}"</p>
                  ) : (
                    <p className="text-muted">No classes found for this school</p>
                  )}
                  <small className="text-muted">School ID: {schoolId}</small>
                  <br />
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="mt-2"
                    onClick={loadClasses}
                  >
                    Retry Loading Classes
                  </Button>
                </div>
              ) : (
                getFilteredClasses().map(classItem => (
                  <Card 
                    key={classItem.id} 
                    className={`mb-2 cursor-pointer ${selectedClass?.id === classItem.id ? 'border-primary' : ''}`}
                    onClick={() => setSelectedClass(classItem)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{classItem.name}</strong>
                          <br />
                          <small className="text-muted">Grade: {classItem.grade_level}</small>
                          <br />
                          <small className="text-muted">
                            Level: {classItem.grade_level1 ? 'Grade 1' : classItem.grade_level2 ? 'Grade 2' : 'General'}
                          </small>
                          {classItem.teacherName && (
                            <>
                              <br />
                              <small className="text-muted">Teacher: {classItem.teacherName}</small>
                            </>
                          )}
                        </div>
                        {selectedClass?.id === classItem.id && (
                          <CheckCircle className="text-primary" />
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                ))
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Assessment Selection */}
        <Col md={4}>
          <Card className="mb-3">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5><Book className="me-2" />Select Screener</h5>
                {/* Assessment Mode Toggle */}
                <ButtonGroup size="sm">
                  <Button 
                    variant={assessmentMode === 'standard' ? 'primary' : 'outline-primary'}
                    onClick={() => setAssessmentMode('standard')}
                  >
                    <BarChart className="me-1" />
                    Standard
                  </Button>
                  <Button 
                    variant={assessmentMode === 'rti' ? 'warning' : 'outline-warning'}
                    onClick={() => setAssessmentMode('rti')}
                  >
                    <Activity className="me-1" />
                    RTI
                  </Button>
                </ButtonGroup>
              </div>
            </Card.Header>
            <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {/* Search input for assessments */}
              <Form.Control
                type="text"
                placeholder="Search screeners..."
                value={assessmentSearch}
                onChange={(e) => setAssessmentSearch(e.target.value)}
                className="mb-3"
                size="sm"
              />
              
              {!selectedClass && assessmentMode === 'standard' ? (
                <p className="text-muted">Please select a class first to see appropriate screeners</p>
              ) : (() => {
                const assessmentGroups = getAssessmentGroups();
                
                if (assessmentMode === 'rti') {
                  const rtiAssessments = getFilteredAssessments(assessmentGroups.rti || []);
                  
                  return (
                    <>
                      <div className="mb-3">
                        <Alert variant="warning" className="p-2">
                          <Activity className="me-2" />
                          <strong>RTI Screeners</strong><br />
                          <small>Response to Intervention screeners for targeted support</small>
                        </Alert>
                      </div>
                      
                      {rtiAssessments.length === 0 ? (
                        <p className="text-muted">
                          {assessmentSearch ? `No RTI screeners found matching "${assessmentSearch}"` : 'No RTI screeners available'}
                        </p>
                      ) : (
                        rtiAssessments.map(assessment => (
                          <Card 
                            key={assessment.id} 
                            className={`mb-2 cursor-pointer ${selectedAssessment?.id === assessment.id ? 'border-warning' : ''}`}
                            onClick={() => setSelectedAssessment(assessment)}
                            style={{ cursor: 'pointer' }}
                          >
                            <Card.Body className="p-3">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <strong>{assessment.name}</strong>
                                  <br />
                                  <small className="text-muted">{assessment.description}</small>
                                  <br />
                                  <Badge bg="warning" className="me-1">RTI</Badge>
                                </div>
                                {selectedAssessment?.id === assessment.id && (
                                  <CheckCircle className="text-warning" />
                                )}
                              </div>
                            </Card.Body>
                          </Card>
                        ))
                      )}
                    </>
                  );
                } else {
                  // Standard assessment display
                  const standardAssessments = getFilteredAssessments(assessmentGroups.standard || []);
                  const customAssessments = getFilteredAssessments(assessmentGroups.custom || []);
                  
                  return (
                    <>
                      <div className="mb-3">
                        <small className="text-muted">
                          Showing screeners for: <strong>{selectedClass?.grade_level}</strong> 
                          ({selectedClass?.grade_level1 ? 'Grade 1' : selectedClass?.grade_level2 ? 'Grade 2' : 'General'})
                        </small>
                      </div>
                      
                      {standardAssessments.length > 0 && (
                        <>
                          <h6 className="text-primary mb-2">Standard Screeners</h6>
                          {standardAssessments.map(assessment => (
                            <Card 
                              key={assessment.id} 
                              className={`mb-2 cursor-pointer ${selectedAssessment?.id === assessment.id ? 'border-success' : ''}`}
                              onClick={() => setSelectedAssessment(assessment)}
                              style={{ cursor: 'pointer' }}
                            >
                              <Card.Body className="p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <strong>{assessment.name}</strong>
                                    <br />
                                    <small className="text-muted">{assessment.description}</small>
                                    <br />
                                    <Badge bg="primary" className="me-1">Standard</Badge>
                                  </div>
                                  {selectedAssessment?.id === assessment.id && (
                                    <CheckCircle className="text-success" />
                                  )}
                                </div>
                              </Card.Body>
                            </Card>
                          ))}
                        </>
                      )}
                      
                      {customAssessments.length > 0 && (
                        <>
                          <h6 className="text-warning mb-2 mt-3">Custom Screeners</h6>
                          {customAssessments.map(assessment => (
                            <Card 
                              key={assessment.id} 
                              className={`mb-2 cursor-pointer ${selectedAssessment?.id === assessment.id ? 'border-success' : ''}`}
                              onClick={() => setSelectedAssessment(assessment)}
                              style={{ cursor: 'pointer' }}
                            >
                              <Card.Body className="p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <strong>{assessment.name}</strong>
                                    <br />
                                    <small className="text-muted">{assessment.description}</small>
                                    <br />
                                    <Badge bg="warning" className="me-1">Custom</Badge>
                                  </div>
                                  {selectedAssessment?.id === assessment.id && (
                                    <CheckCircle className="text-success" />
                                  )}
                                </div>
                              </Card.Body>
                            </Card>
                          ))}
                        </>
                      )}
                      
                      {standardAssessments.length === 0 && customAssessments.length === 0 && (
                        <p className="text-muted">
                          {assessmentSearch ? `No screeners found matching "${assessmentSearch}"` : 'No screeners available for this grade level'}
                        </p>
                      )}
                    </>
                  );
                }
              })()}
            </Card.Body>
          </Card>
        </Col>

        {/* Student Selection */}
        <Col md={4}>
          <Card className="mb-3">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5>
                  <PersonPlus className="me-2" />
                  Select Students
                  {assessmentMode === 'rti' && <Badge bg="warning" className="ms-2">RTI</Badge>}
                </h5>
                {selectedClass && (
                  <small className="text-muted">
                    {selectedStudents.length} of {getEligibleStudents().length} selected
                  </small>
                )}
              </div>
            </Card.Header>
            <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {!selectedClass ? (
                <p className="text-muted">Please select a class first</p>
              ) : loadingStudentData ? (
                <p>Loading students...</p>
              ) : loadingAssessments ? (
                <p>Loading student assessment data...</p>
              ) : classStudents.length === 0 ? (
                <p className="text-muted">No students found in this class</p>
              ) : (
                <>
                  {/* Search input for students */}
                  <Form.Control
                    type="text"
                    placeholder="Search students..."
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    className="mb-3"
                    size="sm"
                  />
                  
                  {selectedAssessment && (
                    <>
                      {assessmentMode === 'rti' && (
                        <Alert variant="info" className="p-2 mb-3">
                          <Activity className="me-2" />
                          <small>RTI screeners can be assigned to multiple students for intervention tracking</small>
                        </Alert>
                      )}
                      <div className="mb-3">
                        <Form.Check
                          type="checkbox"
                          label="Select All Eligible Students"
                          checked={selectedStudents.length === getEligibleStudents().length && getEligibleStudents().length > 0}
                          onChange={handleSelectAllStudents}
                        />
                      </div>
                    </>
                  )}
                  {Array.isArray(classStudents) && getFilteredStudents().map(student => {
                    const status = getStudentAssessmentStatus(student);
                    const isEligible = status === "eligible" || status === "not-selected";
                    
                    const studentAssessmentList = studentAssessments[student.id] || [];
                    
                    // Get current pending assessments based on mode
                    let currentAssessments = [];
                    if (assessmentMode === 'rti') {
                      // For RTI mode: show current RTI assessments
                      currentAssessments = studentAssessmentList.filter(assessment => {
                        const isIncomplete = assessment.completed !== true && 
                                            assessment.status !== 'completed' &&
                                            assessment.completion_status !== 'completed';
                        return isIncomplete && isRTIAssessment(assessment);
                      });
                    } else {
                      // For standard mode: show current standard assessments
                      currentAssessments = studentAssessmentList.filter(assessment => 
                        assessment.completed !== true && 
                        assessment.status !== 'completed' &&
                        assessment.completion_status !== 'completed'
                      );
                    }
                    
                    return (
                      <div key={student.id} className="mb-2 p-2 border rounded">
                        <div className="d-flex align-items-center">
                          <Form.Check
                            type="checkbox"
                            checked={selectedStudents.includes(student.id)}
                            onChange={() => handleStudentSelection(student.id)}
                            disabled={!isEligible}
                            className="me-2"
                          />
                          <div className="flex-grow-1">
                            <strong>{student.name}</strong>
                            {currentAssessments.length > 0 && (
                              <>
                                <br />
                                <small className={assessmentMode === 'rti' ? 'text-warning' : 'text-info'}>
                                  Current {assessmentMode === 'rti' ? 'RTI' : ''}: {currentAssessments.map(a => a.name).join(', ')}
                                </small>
                              </>
                            )}
                          </div>
                          <div>
                            {status === "completed" && (
                              <Badge bg="success">Completed</Badge>
                            )}
                            {status === "assigned" && (
                              <Badge bg={assessmentMode === 'rti' ? 'warning' : 'info'}>
                                {assessmentMode === 'rti' ? 'Has RTI Screener' : 'Has Pending Screener'}
                              </Badge>
                            )}
                            {status === "eligible" && selectedAssessment && (
                              <Badge bg="light" text="dark">Eligible</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {getFilteredStudents().length === 0 && studentSearch && (
                    <p className="text-muted">No students found matching "{studentSearch}"</p>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Action Buttons */}
      <div className="text-center mt-4">
        <Button variant="secondary" onClick={onClose} className="me-2">
          Cancel
        </Button>
        <Button 
          variant={assessmentMode === 'rti' ? 'warning' : 'primary'}
          onClick={() => setShowConfirmModal(true)}
          disabled={!selectedClass || !selectedAssessment || selectedStudents.length === 0}
        >
          {assessmentMode === 'rti' ? <Activity className="me-2" /> : <PersonPlus className="me-2" />}
          Assign {assessmentMode === 'rti' ? 'RTI Screener' : 'Screener'} ({selectedStudents.length} students)
        </Button>
      </div>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {assessmentMode === 'rti' ? <Activity className="me-2" /> : <Calendar2Check className="me-2" />}
            Confirm {assessmentMode === 'rti' ? 'RTI Screener' : 'Screener'} Assignment
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant={assessmentMode === 'rti' ? 'warning' : 'info'}>
            {assessmentMode === 'rti' ? <Activity className="me-2" /> : <ExclamationTriangle className="me-2" />}
            You are about to assign <strong>{selectedAssessment?.name}</strong> 
            {assessmentMode === 'rti' && <Badge bg="warning" className="ms-2">RTI</Badge>} 
            to <strong>{selectedStudents.length}</strong> students in <strong>{selectedClass?.name}</strong>.
            {assessmentMode === 'rti' && (
              <>
                <br /><br />
                <small>
                  <strong>RTI Note:</strong> This screener is part of Response to Intervention and can be assigned 
                  to students who may already have other screeners in progress.
                </small>
              </>
            )}
          </Alert>
          
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>Student Name</th>
                <th>Grade</th>
                <th>Screener</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              {getSelectedStudentDetails().map((student) => (
                <tr key={student.index}>
                  <td>{student.index}</td>
                  <td>{student.name}</td>
                  <td>{student.grade}</td>
                  <td>{student.assessment}</td>
                  <td>
                    <Badge bg={student.type === 'RTI' ? 'warning' : 'primary'}>
                      {student.type}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button 
            variant={assessmentMode === 'rti' ? 'warning' : 'primary'}
            onClick={handleAssignScreeners}
            disabled={loading}
          >
            {loading ? "Assigning..." : `Confirm ${assessmentMode === 'rti' ? 'RTI' : ''} Assignment`}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ClassScreenerAssignment;